import React, { useState } from "react";
import { mockCreator } from "../../../data/mockData";
import Tabs from "../../../components/ui/Tabs";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/Card";
import Input from "../../../components/ui/Input";
import InviteMemberModal from "../components/modals/InviteMemberModal";
import ConnectPlatformModal from "../components/modals/ConnectPlatformModal";
import { 
  UserPlus, 
  Trash2, 
  Upload, 
  Youtube, 
  Instagram, 
  Facebook, 
  Linkedin, 
  Send, 
  Music2, 
  Link as LinkIcon, 
  Check, 
  RefreshCw,
  Globe,
  X,
  Edit2
} from "lucide-react";
import ImageUpload from "../../../components/ui/ImageUpload";

// Content for the Connected Accounts Tab
const ConnectedAccountsTab = () => {
  const [accounts, setAccounts] = useState([
    { id: "yt", platform: "YouTube", icon: Youtube, connected: false, username: "", color: "text-red-600", bgColor: "bg-red-50" },
    { id: "ig", platform: "Instagram", icon: Instagram, connected: false, username: "", color: "text-pink-600", bgColor: "bg-pink-50" },
    { id: "fb", platform: "Facebook", icon: Facebook, connected: false, username: "", color: "text-blue-600", bgColor: "bg-blue-50" },
    { id: "tk", platform: "TikTok", icon: Music2, connected: false, username: "", color: "text-slate-900", bgColor: "bg-slate-100" },
    { id: "li", platform: "LinkedIn", icon: Linkedin, connected: false, username: "", color: "text-blue-700", bgColor: "bg-blue-50" },
    { id: "tg", platform: "Telegram", icon: Send, connected: false, username: "", color: "text-sky-500", bgColor: "bg-sky-50" },
    { id: "swm", platform: "Sewasew Multimedia", icon: Globe, connected: false, username: "", color: "text-amber-600", bgColor: "bg-amber-50" },
    { id: "sws", platform: "Sewasew SRBT", icon: Globe, connected: false, username: "", color: "text-emerald-600", bgColor: "bg-emerald-50" },
  ]);
  const [importing, setImporting] = useState(null);
  const [connectingAccount, setConnectingAccount] = useState(null);

  const handleConnect = (id) => {
    const account = accounts.find(a => a.id === id);
    setConnectingAccount(account);
  };

  const onConnectSubmit = (id, username) => {
    setAccounts(accounts.map(acc => 
      acc.id === id ? { ...acc, connected: true, username: username.startsWith('@') ? username : `@${username}` } : acc
    ));
    setConnectingAccount(null);
  };

  const handleDisconnect = (id) => {
    if (window.confirm("Are you sure you want to disconnect this account?")) {
      setAccounts(accounts.map(acc => 
        acc.id === id ? { ...acc, connected: false, username: "" } : acc
      ));
    }
  };

  const handleImport = (id) => {
    setImporting(id);
    setTimeout(() => {
      setImporting(null);
      alert("Content imported successfully!");
    }, 2000);
  };

  return (
    <Card className="@container">
      <div>
        <h3 className="text-xl font-bold text-slate-800">Connected Accounts</h3>
        <p className="text-slate-500 text-sm mt-1 mb-6">Connect your external platforms to import content, sync monetization, and share updates.</p>
      </div>
      
      <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-4">
        {accounts.map((acc) => (
          <div key={acc.id} className="flex flex-col p-4 border border-slate-200 rounded-2xl hover:border-primary/30 transition-colors group">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2.5 rounded-xl ${acc.connected ? acc.bgColor + " " + acc.color : "bg-slate-50 text-slate-400 group-hover:bg-slate-100"}`}>
                  <acc.icon size={22} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">{acc.platform}</h4>
                  {acc.connected ? (
                    <p className="text-[11px] text-green-600 font-medium flex items-center">
                      <Check size={12} className="mr-1" /> {acc.username}
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-400">Disconnected</p>
                  )}
                </div>
              </div>
              
              {acc.connected ? (
                <button onClick={() => handleDisconnect(acc.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                  <Trash2 size={16} />
                </button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => handleConnect(acc.id)} className="h-8 px-3 text-xs">
                  Connect
                </Button>
              )}
            </div>
            
            {acc.connected && (
              <div className="flex gap-2 mt-auto">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 h-8 text-[11px] font-bold"
                  onClick={() => handleImport(acc.id)}
                  disabled={!!importing}
                >
                  {importing === acc.id ? (
                    <><RefreshCw size={12} className="mr-2 animate-spin" /> Syncing</>
                  ) : (
                    <><RefreshCw size={12} className="mr-2" /> Sync Content</>
                  )}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold px-2">
                  Settings
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <ConnectPlatformModal 
        isOpen={!!connectingAccount}
        platform={connectingAccount}
        onClose={() => setConnectingAccount(null)}
        onConnect={onConnectSubmit}
      />
    </Card>
  );
};

// Content for the Team Management Tab
const TeamManagementTab = ({ members, onInvite, onEdit, onSuspend, onRemove }) => (
  <Card>
    <div className="flex justify-between items-center mb-6">
      <div>
        <h3 className="text-lg font-bold text-slate-800">Manage Team</h3>
        <p className="text-sm text-slate-500">Control who has access to your dashboard.</p>
      </div>
      <Button variant="primary" onClick={onInvite}>
        <UserPlus size={16} className="mr-2" /> Invite Member
      </Button>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm text-left">
        <thead className="bg-slate-50 text-slate-500 font-medium">
          <tr>
            <th className="p-3 rounded-l-lg">User</th>
            <th className="p-3">Role</th>
            <th className="p-3">Status</th>
            <th className="p-3">Joined</th>
            <th className="p-3 rounded-r-lg text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {members.map((member) => (
            <tr key={member.id} className="group hover:bg-slate-50 transition-colors">
              <td className="p-3">
                <div className="flex items-center">
                  <img
                    src={member.avatarUrl}
                    alt={member.name}
                    className="w-9 h-9 rounded-full mr-3 object-cover border border-slate-200"
                  />
                  <div>
                    <p className="font-bold text-slate-800">{member.name}</p>
                    <p className="text-xs text-slate-500">{member.email}</p>
                  </div>
                </div>
              </td>
              <td className="p-3">
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200">
                  {member.role}
                </span>
              </td>
              <td className="p-3">
                 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                   member.status === "Suspended" 
                     ? "bg-red-100 text-red-600" 
                     : "bg-emerald-100 text-emerald-600"
                 }`}>
                   {member.status === "Suspended" ? "Suspended" : "Active"}
                 </span>
              </td>
              <td className="p-3 text-slate-500 text-xs">
                {member.joinedDate || "Oct 24, 2024"}
              </td>
              <td className="p-3 text-right">
                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => onSuspend(member.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      member.status === "Suspended" ? "text-emerald-500 hover:bg-emerald-50" : "text-amber-500 hover:bg-amber-50"
                    }`}
                    title={member.status === "Suspended" ? "Activate" : "Suspend"}
                  >
                    <RefreshCw size={16} />
                  </button>
                   <button 
                     onClick={() => onEdit(member)}
                     className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors" 
                     title="Edit Permissions"
                   >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    onClick={() => onRemove(member.id)}
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors" 
                    title="Remove User"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

// Placeholder for the Profile Tab
const ProfileTab = ({ creator }) => {
  const [profilePic, setProfilePic] = useState(creator.profilePictureUrl);
  const [bannerUrl, setBannerUrl] = useState(creator.bannerUrl);
  
  // Custom URL State
  const [customUrl, setCustomUrl] = useState(creator.username); // Default to username
  const [urlStatus, setUrlStatus] = useState("idle"); // idle, checking, available, unavailable

  const checkAvailability = () => {
    if (!customUrl) return;
    setUrlStatus("checking");
    setTimeout(() => {
      // Mock check: "taken" or "admin" are unavailable
      if (["taken", "admin", "null", "undefined"].includes(customUrl.toLowerCase())) {
        setUrlStatus("unavailable");
      } else {
        setUrlStatus("available");
      }
    }, 800);
  };

  return (
    <Card className="@container">
      <div className="flex justify-between items-center mb-6">
         <div>
            <h3 className="text-lg font-bold text-slate-800">Profile Settings</h3>
            <p className="text-sm text-slate-500">Customize how you appear to your fans.</p>
         </div>
         <Button variant="primary">Save Changes</Button>
      </div>

      <div className="grid grid-cols-1 @lg:grid-cols-3 gap-8">
        {/* Left Column: Visuals */}
        <div className="space-y-6">
           <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
             <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 block">
               Visuals
             </label>
             
             <div className="space-y-4">
               <div>
                  <span className="text-sm font-medium text-slate-700 mb-2 block">Profile Picture</span>
                  <div className="flex items-center gap-4">
                    <div className="w-20">
                      <ImageUpload
                        aspectRatio="square"
                        currentImage={profilePic}
                        onUpload={(file, url) => setProfilePic(url)}
                      />
                    </div>
                    <div className="text-xs text-slate-400">
                       Square image recommended.<br/>Max 5MB.
                    </div>
                  </div>
               </div>
               
               <div className="pt-2 border-t border-slate-200">
                  <span className="text-sm font-medium text-slate-700 mb-2 block">Banner Image</span>
                  <div className="w-full">
                    <ImageUpload
                      aspectRatio="banner"
                      currentImage={bannerUrl}
                      onUpload={(file, url) => setBannerUrl(url)}
                    />
                  </div>
               </div>
             </div>
           </div>
        </div>

        {/* Right Column: Details */}
        <div className="@lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 @md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name</label>
              <Input defaultValue={creator.fullName} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Display Name</label>
              <Input defaultValue={creator.username} />
            </div>
          </div>

          <div>
             <label className="text-sm font-medium text-slate-700 mb-1 block">Public Profile URL</label>
             <div className="flex gap-2">
                <div className="relative flex-1">
                   <div className="absolute left-3 top-2.5 text-slate-400 text-sm font-medium pointer-events-none">
                     adnaki.com/
                   </div>
                   <input 
                     className={`w-full pl-[105px] pr-10 py-2 border rounded-lg text-sm outline-none transition-colors ${
                       urlStatus === "available" ? "border-green-500 focus:border-green-500" :
                       urlStatus === "unavailable" ? "border-red-500 focus:border-red-500" :
                       "border-slate-300 focus:border-primary"
                     }`}
                     value={customUrl}
                     onChange={(e) => {
                       setCustomUrl(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""));
                       setUrlStatus("idle");
                     }}
                   />
                   {urlStatus === "checking" && (
                     <div className="absolute right-3 top-2.5">
                       <RefreshCw size={16} className="text-slate-400 animate-spin" />
                     </div>
                   )}
                   {urlStatus === "available" && (
                     <div className="absolute right-3 top-2.5">
                       <Check size={16} className="text-green-500" />
                     </div>
                   )}
                   {urlStatus === "unavailable" && (
                     <div className="absolute right-3 top-2.5">
                       <X size={16} className="text-red-500" />
                     </div>
                   )}
                </div>
                <Button variant="secondary" onClick={checkAvailability} disabled={!customUrl || urlStatus === "checking"}>
                   Check
                </Button>
             </div>
             {urlStatus === "available" && <p className="text-xs text-green-600 mt-1">This URL is available!</p>}
             {urlStatus === "unavailable" && <p className="text-xs text-red-600 mt-1">Sorry, this URL is already taken.</p>}
             <p className="text-xs text-slate-400 mt-1">
               This is the unique link your fans will use to visit your page.
             </p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Bio</label>
            <textarea
              rows="4"
              className="w-full mt-1 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm"
              defaultValue={creator.bio}
              placeholder="Tell your fans about yourself..."
            ></textarea>
            <p className="text-xs text-slate-400 mt-1 text-right">0/300 characters</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

const SettingsPage = () => {
  const [teamMembers, setTeamMembers] = useState(mockCreator.teamMembers.map(m => ({...m, status: "Active", joinedDate: "Oct 24, 2024", permissions: ["view_analytics"]})));
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const handleSaveMember = (memberData) => {
    if (memberData.id) {
        // Edit existing
        setTeamMembers(teamMembers.map(m => m.id === memberData.id ? { ...m, permissions: memberData.permissions } : m));
    } else {
        // Create new
        const newMember = {
            id: Date.now(),
            name: memberData.email.split("@")[0], // a mock name
            email: memberData.email,
            role: "Custom", // default role name for now
            permissions: memberData.permissions,
            avatarUrl: `https://i.pravatar.cc/100?u=${memberData.email}`,
            status: "Active",
            joinedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        };
        setTeamMembers([newMember, ...teamMembers]);
    }
    setEditingMember(null);
  };

  const handleEdit = (member) => {
      setEditingMember(member);
      setInviteModalOpen(true);
  };

  const handleSuspend = (id) => {
     setTeamMembers(teamMembers.map(m => 
       m.id === id ? { ...m, status: m.status === "Active" ? "Suspended" : "Active" } : m
     ));
  };

  const handleRemove = (id) => {
     if(window.confirm("Are you sure you want to remove this team member?")) {
        setTeamMembers(teamMembers.filter(m => m.id !== id));
     }
  };

  // The "Payouts" tab has been removed
  const tabs = [
    { name: "Profile", content: <ProfileTab creator={mockCreator} /> },
    {
      name: "Team Management",
      content: (
        <TeamManagementTab
          members={teamMembers}
          onInvite={() => { setEditingMember(null); setInviteModalOpen(true); }}
          onEdit={handleEdit}
          onSuspend={handleSuspend}
          onRemove={handleRemove}
        />
      ),
    },
    { name: "Connected Accounts", content: <ConnectedAccountsTab /> },
  ];

  return (
    <>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800">Settings</h1>
          <p className="text-slate-500 mt-1">
            Manage your account, team, and preferences.
          </p>
        </div>
        <Tabs tabs={tabs} />
      </div>
      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onInvite={handleSaveMember}
        memberToEdit={editingMember}
      />
    </>
  );
};

export default SettingsPage;
